import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboardIcon, BarChartIcon, QrCodeIcon, FileTextIcon, UserIcon, MegaphoneIcon, SettingsIcon, BuildingIcon, LogOutIcon, MenuIcon, XIcon, BellIcon, HelpCircleIcon, SearchIcon, SunIcon, MoonIcon, ActivityIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
const MerchantPortal: React.FC = () => {
  const {
    user,
    logout
  } = useAuth();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Mock merchant data
  const merchantInfo = {
    name: 'Global Electronics',
    tier: 'Premium',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8Y29tcGFueSUyMGxvZ298ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=100&q=60',
    unreadNotifications: 3,
    isMultiMerchant: true,
    subsidiaries: [{
      id: 'sub1',
      name: 'Downtown Electronics'
    }, {
      id: 'sub2',
      name: 'City Tech Store'
    }, {
      id: 'sub3',
      name: 'Metro Gadgets'
    }],
    activeMerchant: 'Global Electronics'
  };
  const navigationItems = [{
    name: 'Dashboard',
    icon: <LayoutDashboardIcon className="h-5 w-5" data-id="element-3555" />,
    path: '/merchant-portal/dashboard'
  }, {
    name: 'Transactions',
    icon: <BarChartIcon className="h-5 w-5" data-id="element-3556" />,
    path: '/merchant-portal/transactions'
  }, {
    name: 'Generate QR',
    icon: <QrCodeIcon className="h-5 w-5" data-id="element-3557" />,
    path: '/merchant-portal/qr-generator'
  }, {
    name: 'Payment Monitor',
    icon: <ActivityIcon className="h-5 w-5" data-id="element-3558" />,
    path: '/merchant-portal/payment-monitor'
  }, {
    name: 'Reports',
    icon: <FileTextIcon className="h-5 w-5" data-id="element-3559" />,
    path: '/merchant-portal/reports'
  }, {
    name: 'Profile',
    icon: <UserIcon className="h-5 w-5" data-id="element-3560" />,
    path: '/merchant-portal/profile'
  }, {
    name: 'Campaigns',
    icon: <MegaphoneIcon className="h-5 w-5" data-id="element-3561" />,
    path: '/merchant-portal/campaigns'
  }, {
    name: 'Settings',
    icon: <SettingsIcon className="h-5 w-5" data-id="element-3562" />,
    path: '/merchant-portal/settings'
  }];
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  const handleMerchantChange = (merchantName: string) => {
    console.log(`Switched to merchant: ${merchantName}`);
    // In a real app, this would update the current merchant context
  };
  return <div className="flex h-screen bg-gray-50 dark:bg-gray-900" data-id="element-3563">
      {/* Mobile menu backdrop */}
      {mobileMenuOpen && <div className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden" onClick={() => setMobileMenuOpen(false)} data-id="element-3564"></div>}

      {/* Sidebar - Mobile */}
      <div className={`fixed inset-y-0 left-0 z-30 w-64 transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:hidden`} data-id="element-3565">
        <div className="flex h-full flex-col bg-gradient-to-b from-purple-700 to-purple-900 text-white shadow-xl" data-id="element-3566">
          <div className="flex items-center justify-between px-4 py-5" data-id="element-3567">
            <div className="flex items-center space-x-3" data-id="element-3568">
              {merchantInfo.logo ? <img className="h-10 w-auto rounded-md" src={merchantInfo.logo} alt={merchantInfo.name} data-id="element-3569" /> : <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center text-purple-800 text-xl font-bold" data-id="element-3570">
                  {merchantInfo.name.charAt(0)}
                </div>}
              <div data-id="element-3571">
                <h2 className="text-lg font-bold text-white" data-id="element-3572">
                  {merchantInfo.name}
                </h2>
                <p className="text-xs text-purple-200" data-id="element-3573">
                  {merchantInfo.tier} Tier
                </p>
              </div>
            </div>
            <button className="rounded-full p-1 text-purple-200 hover:bg-purple-800 hover:text-white" onClick={() => setMobileMenuOpen(false)} data-id="element-3574">
              <XIcon className="h-6 w-6" data-id="element-3575" />
            </button>
          </div>
          {merchantInfo.isMultiMerchant && <div className="px-4 py-3 border-b border-purple-600/50" data-id="element-3576">
              <label htmlFor="mobile-merchant-select" className="block text-xs font-medium text-purple-200 mb-1" data-id="element-3577">
                Active Merchant
              </label>
              <select id="mobile-merchant-select" className="block w-full pl-3 pr-10 py-2 text-sm border border-purple-600/50 rounded-md bg-purple-800/50 text-white focus:outline-none focus:ring-2 focus:ring-white" value={merchantInfo.activeMerchant} onChange={e => handleMerchantChange(e.target.value)} data-id="element-3578">
                <option value={merchantInfo.name} data-id="element-3579">{merchantInfo.name}</option>
                {merchantInfo.subsidiaries.map(sub => <option key={sub.id} value={sub.name} data-id="element-3580">
                    {sub.name}
                  </option>)}
              </select>
            </div>}
          <nav className="flex-1 overflow-y-auto px-4 py-4" data-id="element-3581">
            <ul className="space-y-1" data-id="element-3582">
              {navigationItems.map(item => <li key={item.name} data-id="element-3583">
                  <NavLink to={item.path} className={({
                isActive
              }) => `flex items-center rounded-lg px-4 py-3 text-sm font-medium ${isActive ? 'bg-white text-purple-800' : 'text-purple-100 hover:bg-purple-800/50'}`} onClick={() => setMobileMenuOpen(false)} data-id="element-3584">
                    <span className="mr-3" data-id="element-3585">{item.icon}</span>
                    <span data-id="element-3586">{item.name}</span>
                  </NavLink>
                </li>)}
            </ul>
          </nav>
          <div className="border-t border-purple-600/50 p-4" data-id="element-3587">
            <button onClick={logout} className="flex w-full items-center rounded-lg px-4 py-2 text-sm font-medium text-purple-100 hover:bg-purple-800/50" data-id="element-3588">
              <LogOutIcon className="mr-3 h-5 w-5" data-id="element-3589" />
              <span data-id="element-3590">Log out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar - Desktop */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col" data-id="element-3591">
        <div className="flex h-full flex-col bg-gradient-to-b from-purple-700 to-purple-900 text-white shadow-xl" data-id="element-3592">
          <div className="flex items-center px-6 py-6 border-b border-purple-600/50" data-id="element-3593">
            {merchantInfo.logo ? <img className="h-10 w-auto rounded-md" src={merchantInfo.logo} alt={merchantInfo.name} data-id="element-3594" /> : <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center text-purple-800 text-xl font-bold" data-id="element-3595">
                {merchantInfo.name.charAt(0)}
              </div>}
            <div className="ml-3" data-id="element-3596">
              <h2 className="text-lg font-bold text-white" data-id="element-3597">
                {merchantInfo.name}
              </h2>
              <p className="text-xs text-purple-200" data-id="element-3598">
                {merchantInfo.tier} Tier
              </p>
            </div>
          </div>
          {merchantInfo.isMultiMerchant && <div className="px-6 py-4 border-b border-purple-600/50" data-id="element-3599">
              <label htmlFor="merchant-select" className="block text-xs font-medium text-purple-200 mb-1" data-id="element-3600">
                Active Merchant
              </label>
              <select id="merchant-select" className="block w-full pl-3 pr-10 py-2 text-sm border border-purple-600/50 rounded-md bg-purple-800/50 text-white focus:outline-none focus:ring-2 focus:ring-white" value={merchantInfo.activeMerchant} onChange={e => handleMerchantChange(e.target.value)} data-id="element-3601">
                <option value={merchantInfo.name} data-id="element-3602">{merchantInfo.name}</option>
                {merchantInfo.subsidiaries.map(sub => <option key={sub.id} value={sub.name} data-id="element-3603">
                    {sub.name}
                  </option>)}
              </select>
            </div>}
          <nav className="flex-1 overflow-y-auto px-4 py-6" data-id="element-3604">
            <ul className="space-y-1.5" data-id="element-3605">
              {navigationItems.map(item => <li key={item.name} data-id="element-3606">
                  <NavLink to={item.path} className={({
                isActive
              }) => `flex items-center rounded-lg px-4 py-3 text-sm font-medium ${isActive ? 'bg-white text-purple-800' : 'text-purple-100 hover:bg-purple-800/50'}`} data-id="element-3607">
                    <span className="mr-3" data-id="element-3608">{item.icon}</span>
                    <span data-id="element-3609">{item.name}</span>
                  </NavLink>
                </li>)}
            </ul>
          </nav>
          <div className="border-t border-purple-600/50 p-4" data-id="element-3610">
            <button onClick={logout} className="flex w-full items-center rounded-lg px-4 py-2 text-sm font-medium text-purple-100 hover:bg-purple-800/50" data-id="element-3611">
              <LogOutIcon className="mr-3 h-5 w-5" data-id="element-3612" />
              <span data-id="element-3613">Log out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden" data-id="element-3614">
        {/* Top Navigation */}
        <header className="bg-white dark:bg-gray-800 shadow-sm z-10" data-id="element-3615">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6" data-id="element-3616">
            <div className="flex items-center" data-id="element-3617">
              <button type="button" className="text-gray-500 dark:text-gray-400 lg:hidden" onClick={() => setMobileMenuOpen(true)} data-id="element-3618">
                <MenuIcon className="h-6 w-6" data-id="element-3619" />
              </button>
              <div className="ml-4 lg:ml-0" data-id="element-3620">
                <h1 className="text-xl font-bold text-purple-700 dark:text-purple-400" data-id="element-3621">
                  Merchant Portal
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4" data-id="element-3622">
              {/* Search */}
              <div className="relative" data-id="element-3623">
                <button onClick={() => setSearchOpen(!searchOpen)} className="p-1 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 focus:outline-none" data-id="element-3624">
                  <SearchIcon className="h-5 w-5" data-id="element-3625" />
                </button>
                {searchOpen && <div className="absolute right-0 mt-2 w-72 rounded-md bg-white dark:bg-gray-800 shadow-lg p-2 z-10" data-id="element-3626">
                    <div className="relative" data-id="element-3627">
                      <input type="text" placeholder="Search..." className="w-full rounded-md border border-gray-300 dark:border-gray-600 py-2 pl-3 pr-10 text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-3628" />
                      <SearchIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-3629" />
                    </div>
                  </div>}
              </div>
              {/* Theme Toggle */}
              <button onClick={toggleTheme} className="p-1 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 focus:outline-none" data-id="element-3630">
                {theme === 'dark' ? <SunIcon className="h-5 w-5" data-id="element-3631" /> : <MoonIcon className="h-5 w-5" data-id="element-3632" />}
              </button>
              {/* Help */}
              <button className="p-1 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 focus:outline-none" data-id="element-3633">
                <HelpCircleIcon className="h-5 w-5" data-id="element-3634" />
              </button>
              {/* Notifications */}
              <button className="p-1 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 focus:outline-none" data-id="element-3635">
                <div className="relative" data-id="element-3636">
                  <BellIcon className="h-5 w-5" data-id="element-3637" />
                  {merchantInfo.unreadNotifications > 0 && <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white" data-id="element-3638">
                      {merchantInfo.unreadNotifications}
                    </span>}
                </div>
              </button>
              {/* User Profile */}
              <div className="flex items-center" data-id="element-3639">
                <div className="h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium" data-id="element-3640">
                  {user?.name.charAt(0)}
                </div>
                <div className="ml-3 hidden md:block" data-id="element-3641">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-200" data-id="element-3642">
                    {user?.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3643">
                    {user?.role}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-4 sm:p-6" data-id="element-3644">
          <div className="mx-auto max-w-7xl" data-id="element-3645">
            <Outlet data-id="element-3646" />
          </div>
        </main>
      </div>
    </div>;
};
export default MerchantPortal;